import React, { useState, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import { accountState, currentRmaState, authenticationState } from 'state/atoms';
import { getShippingPrice } from 'utils/helpers';
import {
  AdvanceExchangeModal,
  ExpediteExchangeModal,
  HasFeeModal,
  DropdownController,
} from 'components';
import {
  STANDARD_SHIPPING_METHOD,
  ADVANCE_SHIPPING_METHOD,
  EXPEDITE_SHIPPING_METHOD,
} from 'utils/constants';
import { Form, FormGroup, Input, HelperText, Error } from 'components/checkout';

import { Price, PackingText } from './styledComponents';
import { shippingMethods } from './options';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';

const ShippingMethodForm = ({ formController }) => {
  const previousMethod = useRef(null);
  const accountData = useRecoilValue(accountState);
  const { hasAllowFeePermission } = useRecoilValue(authenticationState);
  const { items } = useRecoilValue(currentRmaState);

  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
  const [isExpediteModalOpen, setIsExpediteModalOpen] = useState(false);
  const [isHasFeeModalOpen, setHasFeeModalOpen] = useState(false);

  const formatPrice = useFormatPrice();

  const { register, control, watch, setValue, errors } = formController;

  const packingInstructions = watch('packingInstructions');
  const method = formController.watch('method');

  const pricePerUnit = getShippingPrice(
    method,
    accountData.getDefaultBillToAddress,
    accountData.currencyConversionRate
  );
  const totalPrice = pricePerUnit * items.length;

  const handleMethodChange = (method) => {
    if (!hasAllowFeePermission) {
      setHasFeeModalOpen(true);
    } else {
      if (method === ADVANCE_SHIPPING_METHOD) {
        setIsAdvanceModalOpen(true);
      } else if (method === EXPEDITE_SHIPPING_METHOD) {
        setIsExpediteModalOpen(true);
      } else if (method === STANDARD_SHIPPING_METHOD) {
        previousMethod.current = STANDARD_SHIPPING_METHOD;
      }
    }
  };

  const setPreviousMethod = () => {
    setValue('method', previousMethod.current || STANDARD_SHIPPING_METHOD);
  };

  return (
    <Form>
      <Error>{errors.method && errors.method.message}</Error>
      <FormGroup small>
        <label>Shipping Method</label>
        <div>
          <DropdownController
            control={control}
            name="method"
            onChange={handleMethodChange}
            options={shippingMethods}
            showFilter={false}
          />
          <Price>{formatPrice(totalPrice)}</Price>
        </div>
      </FormGroup>
      <Error>{errors.packingInstructions && errors.packingInstructions.message}</Error>
      <FormGroup>
        <label>Special Instructions</label>
        <div>
          <Input
            name="packingInstructions"
            error={errors.packingInstructions}
            ref={register}
            maxLength={150}
          />
        </div>
        <HelperText>{150 - packingInstructions.length} characters remaining</HelperText>
      </FormGroup>
      <PackingText>
        Any information entered in this field will appear on Packing Slips and Invoices such as site
        name, project reference, work order number, attention, etc.
      </PackingText>
      <AdvanceExchangeModal
        cost={formatPrice(pricePerUnit)}
        isOpen={isAdvanceModalOpen}
        onConfirmation={() => {
          setIsAdvanceModalOpen(false);
          previousMethod.current = ADVANCE_SHIPPING_METHOD;
        }}
        onClose={() => {
          setPreviousMethod();
          setIsAdvanceModalOpen(false);
        }}
      />
      <ExpediteExchangeModal
        cost={formatPrice(pricePerUnit)}
        isOpen={isExpediteModalOpen}
        onConfirmation={() => {
          setIsExpediteModalOpen(false);
          previousMethod.current = EXPEDITE_SHIPPING_METHOD;
        }}
        onClose={() => {
          setPreviousMethod();
          setIsExpediteModalOpen(false);
        }}
      />
      <HasFeeModal
        isOpen={isHasFeeModalOpen}
        onClose={() => {
          setPreviousMethod();
          setHasFeeModalOpen(false);
        }}
      />
    </Form>
  );
};

export { ShippingMethodForm };
